import {
  type RelevantUsersForAssetQuery,
  type RelevantUsersForCommentThreadsQuery,
  useRelevantUsersForAssetQuery,
  useRelevantUsersForCommentThreadsQuery,
} from "src/graphql";

/**
 * Everyone named on one ad or a list of threads, by user id. The users
 * permission hides anyone outside the viewer's organizations, so the comment
 * and activity joins come back null for them, most often staff who commented
 * while impersonating.
 */
export type RelevantUsers = ReadonlyMap<
  number,
  { name: string; isHightouchStaff: boolean }
>;

export const NO_RELEVANT_USERS: RelevantUsers = new Map();

function toRelevantUsers(
  users: RelevantUsersForAssetQuery["getRelevantUsersForAsset"],
): RelevantUsers {
  return new Map(
    users.map(({ userId, name, isHightouchStaff }) => [
      userId,
      { name, isHightouchStaff },
    ]),
  );
}

/** Not on the comment poll: who is on an ad changes far less often than its comments. */
export function useRelevantAssetUsers(
  adBriefAssetId: string | null | undefined,
): RelevantUsers {
  const { data } = useRelevantUsersForAssetQuery(
    { assetId: adBriefAssetId ?? "" },
    {
      enabled: Boolean(adBriefAssetId),
      select: selectAssetUsers,
      staleTime: RELEVANT_USERS_STALE_MS,
    },
  );

  return data ?? NO_RELEVANT_USERS;
}

/**
 * Everyone named on the given threads, whatever they are on, so a list
 * spanning subjects, like a canvas's, takes one call. Asks nothing for no
 * threads.
 */
export function useRelevantCommentThreadUsers(
  threadIds: readonly string[],
): RelevantUsers {
  // Sorted so the same threads in a new order, as they re-sort by activity, reuse the cached names.
  const sortedIds = [...threadIds].sort();
  const { data } = useRelevantUsersForCommentThreadsQuery(
    { threadIds: sortedIds },
    {
      enabled: sortedIds.length > 0,
      select: selectCommentThreadUsers,
      staleTime: RELEVANT_USERS_STALE_MS,
      // A new thread changes the key, and the names already shown should not blink to unknown while it loads.
      keepPreviousData: true,
    },
  );

  return data ?? NO_RELEVANT_USERS;
}

const selectAssetUsers = (data: RelevantUsersForAssetQuery) =>
  toRelevantUsers(data.getRelevantUsersForAsset);

const selectCommentThreadUsers = (data: RelevantUsersForCommentThreadsQuery) =>
  toRelevantUsers(data.getRelevantUsersForCommentThreads);

const RELEVANT_USERS_STALE_MS = 30 * 60 * 1000;
